import { Box, CircularProgress, Typography } from "@mui/material";
import { colors } from "../../../theme/muiColors";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { highlightText } from "@components/shared/utils/highlightText";

function getMessagePreview(message) {
  const content = message?.content;
  const text = (content?.text ?? content?.body ?? content?.message ?? "").trim();

  if (text) return text;

  const name = content?.name?.trim();
  const category = content?.category;

  if (category === "image") return name || "Image";
  if (category === "video") return name || "Video";
  if (category === "audio") return name || "Voice message";
  if (name) return name;

  return "Empty message";
}

function getMessageDateLabel(date) {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
}

function getSenderLabel(message, self) {
  const sender = message?.sender;
  if (!sender) return "Unknown";

  const isMine =
    self &&
    sender.type === self.type &&
    ((self.uid != null && String(sender.uid) === String(self.uid)) ||
      (self.id != null && Number(sender.id) === Number(self.id)));

  if (isMine) return "You";

  const fullName = [sender.firstName, sender.lastName].filter(Boolean).join(" ").trim();

  return sender.businessName || fullName || sender.name || "Contact";
}

export const MessageSearchResultList = ({
  messages,
  searchQuery,
  self,
  isLoading,
  error,
  isFetchingNextPage,
  hasNextPage,
  onFetchNextPage,
  onSelectMessage,
}) => {
  const handleScroll = (event) => {
    if (!hasNextPage || isFetchingNextPage) return;

    const container = event.currentTarget;
    const bottomThreshold = 120;

    const reachedBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= bottomThreshold;

    if (reachedBottom) onFetchNextPage();
  };

  return (
    <Box
      flex={1}
      overflow="auto"
      onScroll={handleScroll}
      sx={{
        backgroundColor: "background.default",
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          bgcolor: "divider",
          borderRadius: "3px",
          "&:hover": { bgcolor: "action.disabled" },
        },
      }}
    >
      <Box
        px={2}
        py={1.25}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Search results for
        </Typography>

        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          “{searchQuery}”
        </Typography>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress size={26} />
        </Box>
      )}

      {error && (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <Box textAlign="center">
            <ErrorOutlineIcon sx={{ fontSize: 36, color: "error.main", mb: 1 }} />
            <Typography color="error" variant="body2">
              Failed to search messages
            </Typography>
            <Typography color="text.secondary" variant="caption">
              Please try again later
            </Typography>
          </Box>
        </Box>
      )}

      {!isLoading && !error && messages.length === 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <Typography color={colors.orange[300]} variant="body2">
            No messages found
          </Typography>
        </Box>
      )}

      {!isLoading &&
        !error &&
        messages.map((message) => {
          const date = new Date(message.createdAt);
          const preview = getMessagePreview(message);
          const senderLabel = getSenderLabel(message, self);
          const isMine = senderLabel === "You";

          return (
            <Box
              key={message.uid}
              px={2}
              py={1.25}
              onClick={() => onSelectMessage(message)}
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                cursor: "pointer",
                "&:hover": { bgcolor: "grey.50" },
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: isMine ? colors.orange[700] : "text.secondary", flexShrink: 0 }}
                >
                  {senderLabel}
                </Typography>

                <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                  {getMessageDateLabel(date)}
                </Typography>

                <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                  {date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                mt={0.5}
                sx={{
                  color: "text.primary",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {highlightText(preview, searchQuery)}
              </Typography>
            </Box>
          );
        })}

      {isFetchingNextPage && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
};
